import React from 'react';
import { Clock, CheckCircle2, XCircle, PauseCircle, Pencil, Copy } from 'lucide-react';
import WhatsAppTemplatePreview, { fillPlaceholders } from './WhatsAppTemplatePreview';

const STATUS_META = {
  APPROVED: { label: 'Approved', className: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  PENDING: { label: 'In review', className: 'bg-amber-100 text-amber-800', icon: Clock },
  REJECTED: { label: 'Rejected', className: 'bg-red-100 text-red-700', icon: XCircle },
  PAUSED: { label: 'Paused', className: 'bg-gray-100 text-gray-700', icon: PauseCircle },
  DISABLED: { label: 'Disabled', className: 'bg-gray-100 text-gray-600', icon: PauseCircle },
  IN_APPEAL: { label: 'In appeal', className: 'bg-orange-100 text-orange-800', icon: Clock },
};

export function StatusBadge({ status, size = 'sm' }) {
  const meta = STATUS_META[status] || { label: status || 'Unknown', className: 'bg-gray-100 text-gray-600', icon: Clock };
  const Icon = meta.icon;
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs';
  return (
    <span className={`inline-flex items-center gap-1 font-semibold rounded-full ${pad} ${meta.className}`}>
      <Icon size={size === 'sm' ? 10 : 12} />
      {meta.label}
    </span>
  );
}

function hasRealRejection(template) {
  const reason = String(template.rejected_reason || '').trim();
  return template.status === 'REJECTED' && reason && reason.toUpperCase() !== 'NONE';
}

/** Compact row for template list sidebar */
export function TemplateListItem({ template, selected, onClick }) {
  const preview = fillPlaceholders(template.body_text, template.body_examples).replace(/\s+/g, ' ').slice(0, 72);
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left rounded-xl border p-3 transition ${
        selected
          ? 'border-emerald-400 bg-emerald-50/80 shadow-sm ring-1 ring-emerald-200'
          : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <p className="font-mono text-sm font-semibold text-gray-900 truncate">{template.name}</p>
        <StatusBadge status={template.status} />
      </div>
      <p className="text-[11px] text-gray-500 line-clamp-2 leading-relaxed">{preview || '—'}</p>
      <div className="flex flex-wrap gap-1.5 mt-2">
        <span className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{template.category}</span>
        <span className="text-[10px] text-gray-400">{template.language}</span>
        {template.param_count > 0 && (
          <span className="text-[10px] text-gray-400">{template.param_count} vars</span>
        )}
      </div>
    </button>
  );
}

/** Full detail view for selected Meta template */
export default function MetaTemplateCard({
  template,
  highlighted = false,
  showPreview = true,
  onEdit,
  onClone,
}) {
  const buttons = Array.isArray(template.buttons) ? template.buttons : [];
  const showRejection = hasRealRejection(template);
  const canEdit = ['APPROVED', 'REJECTED', 'PAUSED'].includes(String(template.status || '').toUpperCase());

  return (
    <div className={`rounded-2xl border bg-white overflow-hidden ${highlighted ? 'ring-2 ring-emerald-400 shadow-lg' : 'shadow-sm'}`}>
      <div className="px-4 py-3 border-b bg-gradient-to-r from-gray-50 to-white">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <h3 className="font-mono text-base font-bold text-gray-900 flex-1 min-w-0 truncate">{template.name}</h3>
          <StatusBadge status={template.status} size="md" />
          {typeof onClone === 'function' && (
            <button
              type="button"
              onClick={() => onClone(template)}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
              title="Clone as new template"
            >
              <Copy size={12} /> Clone
            </button>
          )}
          {canEdit && typeof onEdit === 'function' && (
            <button
              type="button"
              onClick={() => onEdit(template)}
              className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-500"
            >
              <Pencil size={12} /> Edit
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-gray-500">
          <span className="font-medium uppercase tracking-wide">{template.category}</span>
          <span>·</span>
          <span>{template.language}</span>
          {template.header_type && template.header_type !== 'NONE' && (
            <>
              <span>·</span>
              <span>{template.header_type} header</span>
            </>
          )}
          {template.param_count > 0 && (
            <>
              <span>·</span>
              <span>{template.param_count} variables</span>
            </>
          )}
        </div>
      </div>

      {showPreview && (
        <div className="p-5 bg-gradient-to-b from-gray-50 to-[#efeae2]/30">
          <WhatsAppTemplatePreview
            headerType={template.header_type}
            headerText={template.header_text}
            headerTextExamples={template.header_text_examples}
            bodyText={template.body_text}
            bodyExamples={template.body_examples}
            footerText={template.footer_text}
            buttons={buttons}
          />
        </div>
      )}

      {buttons.length > 0 && (
        <div className="px-4 py-3 border-t bg-gray-50">
          <p className="text-[11px] font-semibold text-gray-600 mb-2">Buttons</p>
          <div className="flex flex-wrap gap-2">
            {buttons.map((btn, i) => (
              <span key={i} className="text-xs bg-white border rounded-lg px-2.5 py-1 text-gray-700">
                <span className="text-gray-400">{btn.type}:</span> {btn.text}
                {btn.url && <span className="text-gray-400 ml-1">→ {btn.url}</span>}
                {btn.phone_number && <span className="text-gray-400 ml-1">→ {btn.phone_number}</span>}
              </span>
            ))}
          </div>
        </div>
      )}

      {showRejection && (
        <div className="px-4 py-3 bg-red-50 border-t border-red-100 text-sm text-red-700">
          <span className="font-semibold">Rejection reason:</span> {template.rejected_reason}
        </div>
      )}

      {highlighted && (
        <div className="px-4 py-2.5 bg-emerald-50 border-t border-emerald-100 text-xs text-emerald-700 font-medium flex items-center gap-1.5">
          <Clock size={14} />
          Just submitted — Meta review usually 24–48 hours
        </div>
      )}

      {template.status === 'PENDING' && !highlighted && (
        <div className="px-4 py-2 bg-amber-50/80 border-t border-amber-100 text-[11px] text-amber-800">
          Meta is reviewing this template. Approval usually takes 24–48 hours.
        </div>
      )}
    </div>
  );
}

export { fillPlaceholders, hasRealRejection };
